import React, { useEffect, useState } from "react";
import { Edit, Save, X, Plus, RotateCw, Wifi, WifiOff } from "lucide-react";
import { fetchSockets, updateSocket, addSocket } from "../services/sockets";
import { fetchNetworks } from "../services/networks";
import AdminCardActions from "../components/AdminCardActions";
import ToolbarSearch from "../components/ToolbarSearch";

const fieldHintStyle = {
    marginTop: 4,
    marginBottom: 10,
    fontSize: 12,
    color: "#5f6b7a",
    lineHeight: 1.4,
};

const emptySocket = {
    network_id: "",
    name: "",
    descriptions: "",
    ip_address: "",
    mac_address: "",
    firmware_version: "",
    public_ip: "",
    price: 0.0,
    status: true,
};

const getNetworkName = (networks, networkId) => {
    return networks.find((network) => network.id === networkId)?.network_name || "(Немає)";
};

const getSelectableNetworks = (networks, selectedNetworkId = null) => {
    const activeNetworks = networks.filter((network) => network.status);
    if (!selectedNetworkId) {
        return activeNetworks;
    }

    const selectedNetwork = networks.find((network) => network.id === selectedNetworkId);
    if (!selectedNetwork) {
        return activeNetworks;
    }

    return activeNetworks.some((network) => network.id === selectedNetworkId)
        ? activeNetworks
        : [...activeNetworks, selectedNetwork];
};

const SocketsAdmins = () => {
    const [sockets, setSockets] = useState([]);
    const [filteredSockets, setFilteredSockets] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [networks, setNetworks] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [edited, setEdited] = useState({ ...emptySocket });
    const [originalData, setOriginalData] = useState({ ...emptySocket });
    const [newSocket, setNewSocket] = useState({ ...emptySocket });
    const [showModal, setShowModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        loadData();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = sockets.filter((s) => {
            const netName = (networks.find((n) => n.id === s.network_id)?.network_name || "").toString().toLowerCase();
            return (
                (s.name || "").toString().toLowerCase().includes(q) ||
                (s.descriptions || "").toString().toLowerCase().includes(q) ||
                (s.ip_address || "").toString().toLowerCase().includes(q) ||
                (s.mac_address || "").toString().toLowerCase().includes(q) ||
                String(s.device_id).includes(q) ||
                netName.includes(q)
            );
        });
        setFilteredSockets(filtered);
    }, [sockets, networks, searchQuery]);

    const loadData = async () => {
        const [s, nets] = await Promise.all([
            fetchSockets(),
            fetchNetworks(),
        ]);
        setSockets(s);
        setNetworks(nets);
        setNewSocket((prev) => {
            if (prev.network_id) {
                return prev;
            }
            const defaultNetworkId = nets.find((network) => network.status)?.id ?? "";
            return { ...prev, network_id: defaultNetworkId };
        });
    };

    const handleEdit = (socket) => {
        setEditingId(socket.id);
        setEdited({ ...socket });
        setOriginalData({ ...socket });
    };

    const handleCancelEdit = () => {
        setEdited(originalData);
        setEditingId(null);
    };

    const handleSave = async (id) => {
        const original = sockets.find((s) => s.id === id);
        if (original?.status === true && edited.status === false) {
            const confirmDisable = window.confirm(
                "Ви впевнені, що хочете вимкнути цю розетку?\nУсі пристрої, що підключені до неї, будуть відв'язані та переведені у статус 'Обслуговування'."
            );
            if (!confirmDisable) {
                setEditingId(null);
                setEdited({ ...emptySocket });
                return;
            }
        }

        await updateSocket(id, edited);
        setEditingId(null);
        loadData();
    };

    const handleAdd = async () => {
        if (!newSocket.name.trim()) {
            alert("Назва не може бути порожною!");
            return;
        }
        if (!newSocket.network_id) {
            alert("Оберіть мережу");
            return;
        }
        await addSocket(newSocket);
        setNewSocket({ ...emptySocket });
        setShowModal(false);
        loadData();
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredSockets) return [];
        const sorted = [...filteredSockets];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    const handleChange = (e, setter, field, isNumber = false) => {
        const value = isNumber
            ? (e.target.value === "" ? "" : parseFloat(e.target.value))
            : e.target.value === "true"
                ? true
                : e.target.value === "false"
                    ? false
                    : e.target.value;
        setter((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadData} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук по назві, IP, MAC, пристрою..."
                    />
                </div>
            </div>

            <div style={{ marginBottom: 12, fontSize: 13, color: "#5f6b7a", lineHeight: 1.5 }}>
                Для роботи ESP32 обов'язкові щонайменше назва, мережа, локальна IP адреса та активний статус.
                Мережу можна змінювати і під час редагування розетки. MAC адреса потрібна для ідентифікації плати, а публічну IP заповнюйте лише якщо контролер реально доступний ззовні.
            </div>

            {showModal && (
                <div className="modal-backdrop" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Нова ESP32 розетка</h3>
                        <input
                            placeholder="Назва (обов'язково)"
                            value={newSocket.name}
                            onChange={(e) => handleChange(e, setNewSocket, "name")}
                        />
                        <input
                            placeholder="Опис"
                            value={newSocket.descriptions}
                            onChange={(e) => handleChange(e, setNewSocket, "descriptions")}
                        />
                        <input
                            placeholder="IP адреса (локальна)"
                            value={newSocket.ip_address}
                            onChange={(e) => handleChange(e, setNewSocket, "ip_address")}
                        />
                        <div style={fieldHintStyle}>
                            Основна адреса контролера в локальній мережі. Саме її backend використовує для прямого fallback-з'єднання з ESP32.
                        </div>
                        <input
                            placeholder="MAC адреса"
                            value={newSocket.mac_address}
                            onChange={(e) => handleChange(e, setNewSocket, "mac_address")}
                        />
                        <div style={fieldHintStyle}>
                            Необов'язкове поле. Корисне для пошуку плати в роутері, DHCP reservation і перевірки, що це саме потрібна ESP32.
                        </div>
                        <input
                            placeholder="Версія firmware"
                            value={newSocket.firmware_version}
                            onChange={(e) => handleChange(e, setNewSocket, "firmware_version")}
                        />
                        <input
                            placeholder="Публічна IP адреса"
                            value={newSocket.public_ip}
                            onChange={(e) => handleChange(e, setNewSocket, "public_ip")}
                        />
                        <div style={fieldHintStyle}>
                            Заповнюйте лише якщо контролер доступний не з локальної мережі, а через зовнішню адресу або окремий маршрутизований endpoint.
                        </div>
                        <input
                            type="number"
                            step="0.01"
                            placeholder="Ціна"
                            value={newSocket.price}
                            onChange={(e) => handleChange(e, setNewSocket, "price", true)}
                        />
                        <select
                            value={newSocket.status}
                            onChange={(e) => handleChange(e, setNewSocket, "status")}
                        >
                            <option value="true">Активний</option>
                            <option value="false">Неактивний</option>
                        </select>
                        <select
                            value={newSocket.network_id}
                            onChange={(e) => handleChange(e, setNewSocket, "network_id", true)}
                        >
                            <option value="">Оберіть мережу</option>
                            {getSelectableNetworks(networks, newSocket.network_id).map((n) => (
                                <option key={n.id} value={n.id}>
                                    {n.network_name}
                                </option>
                            ))}
                        </select>
                        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                            <button className="add-btn" onClick={handleAdd}>Додати</button>
                            <button onClick={() => setShowModal(false)} className="add-btn cancel-btn">Скасувати</button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile ? (
                <div className="config-grid">
                    {getSortedData().map((s) => (
                        <div key={s.id} className="config-card">
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                {s.is_online ? (
                                    <Wifi size={16} style={{ color: "green" }} />
                                ) : (
                                    <WifiOff size={16} style={{ color: "red" }} />
                                )}
                                <strong>{s.name}</strong>
                            </div>

                            <div><strong>Опис:</strong> {editingId === s.id ? (
                                <input
                                    value={edited.descriptions}
                                    onChange={(e) => handleChange(e, setEdited, "descriptions")}
                                />
                            ) : s.descriptions || "(Немає)"}</div>

                            <div><strong>IP адреса:</strong> {editingId === s.id ? (
                                <input
                                    value={edited.ip_address}
                                    onChange={(e) => handleChange(e, setEdited, "ip_address")}
                                />
                            ) : s.ip_address || "(Немає)"}</div>

                            <div><strong>MAC адреса:</strong> {editingId === s.id ? (
                                <input
                                    value={edited.mac_address}
                                    onChange={(e) => handleChange(e, setEdited, "mac_address")}
                                />
                            ) : s.mac_address || "(Немає)"}</div>

                            <div><strong>Firmware:</strong> {editingId === s.id ? (
                                <input
                                    value={edited.firmware_version}
                                    onChange={(e) => handleChange(e, setEdited, "firmware_version")}
                                />
                            ) : s.firmware_version || "(Немає)"}</div>

                            <div><strong>Публічна IP:</strong> {editingId === s.id ? (
                                <input
                                    value={edited.public_ip}
                                    onChange={(e) => handleChange(e, setEdited, "public_ip")}
                                />
                            ) : s.public_ip || "(Невідома)"}</div>

                            <div><strong>Останній контакт:</strong> {s.last_seen ? new Date(s.last_seen).toLocaleString("uk-UA") : "(Ніколи)"}</div>

                            <div><strong>Ціна:</strong> {editingId === s.id ? (
                                <input
                                    type="number"
                                    value={edited.price}
                                    onChange={(e) => handleChange(e, setEdited, "price", true)}
                                />
                            ) : s.price}</div>

                            <div><strong>Статус:</strong> {editingId === s.id ? (
                                <select
                                    value={edited.status}
                                    onChange={(e) => handleChange(e, setEdited, "status")}
                                >
                                    <option value="true">Активний</option>
                                    <option value="false">Неактивний</option>
                                </select>
                            ) : s.status ? "Активний" : "Неактивний"}</div>

                            <div><strong>Мережа:</strong> {editingId === s.id ? (
                                <select
                                    value={edited.network_id}
                                    onChange={(e) => handleChange(e, setEdited, "network_id", true)}
                                >
                                    {getSelectableNetworks(networks, edited.network_id).map((n) => (
                                        <option key={n.id} value={n.id}>{n.network_name}</option>
                                    ))}
                                </select>
                            ) : getNetworkName(networks, s.network_id)}</div>

                            <AdminCardActions
                                editing={editingId === s.id}
                                onEdit={() => handleEdit(s)}
                                onSave={() => handleSave(s.id)}
                                onCancel={handleCancelEdit}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="table-responsive">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>
                                    Назва {sortBy === "name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Описание {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("ip_address")}>
                                    Локальна ІР {sortBy === "ip_address" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("mac_address")}>
                                    MAC адреса {sortBy === "mac_address" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("firmware_version")}>
                                    Версія файєрволу {sortBy === "firmware_version" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("public_ip")}>
                                    Публічна ІР {sortBy === "public_ip" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("network_id")}>
                                    Мережа {sortBy === "network_id" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("price")}>
                                    Ціна {sortBy === "price" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((s) => (
                                <tr key={s.id}>
                                    <td>
                                        {editingId === s.id ? (
                                            <input
                                                value={edited.name}
                                                onChange={(e) => handleChange(e, setEdited, "name")}
                                            />
                                        ) : (
                                            s.name
                                        )}
                                    </td>
                                    <td>
                                        {editingId === s.id ? (
                                            <input
                                                value={edited.descriptions}
                                                onChange={(e) => handleChange(e, setEdited, "descriptions")}
                                            />
                                        ) : (
                                            s.descriptions || "(Немає)"
                                        )}
                                    </td>
                                    <td>
                                        {editingId === s.id ? (
                                            <input
                                                value={edited.ip_address}
                                                onChange={(e) => handleChange(e, setEdited, "ip_address")}
                                            />
                                        ) : (
                                            s.ip_address || "(Немає)"
                                        )}
                                    </td>
                                    <td>
                                        {editingId === s.id ? (
                                            <input
                                                value={edited.mac_address}
                                                onChange={(e) => handleChange(e, setEdited, "mac_address")}
                                            />
                                        ) : (
                                            s.mac_address || "(Немає)"
                                        )}
                                    </td>
                                    <td>
                                        {editingId === s.id ? (
                                            <input
                                                value={edited.firmware_version}
                                                onChange={(e) => handleChange(e, setEdited, "firmware_version")}
                                            />
                                        ) : (
                                            s.firmware_version || "(Невідома)"
                                        )}
                                    </td>
                                    <td>
                                        {editingId === s.id ? (
                                            <input
                                                value={edited.public_ip}
                                                onChange={(e) => handleChange(e, setEdited, "public_ip")}
                                            />
                                        ) : (
                                            s.public_ip || "(Невідома)"
                                        )}
                                    </td>
                                    <td>
                                        {editingId === s.id ? (
                                            <select
                                                value={edited.network_id}
                                                onChange={(e) => handleChange(e, setEdited, "network_id", true)}
                                            >
                                                {getSelectableNetworks(networks, edited.network_id).map((n) => (
                                                    <option key={n.id} value={n.id}>
                                                        {n.network_name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            getNetworkName(networks, s.network_id)
                                        )}
                                    </td>
                                    <td>
                                        {editingId === s.id ? (
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={edited.price}
                                                onChange={(e) => handleChange(e, setEdited, "price", true)}
                                            />
                                        ) : (
                                            s.price || "0"
                                        )}
                                    </td>
                                    <td>
                                        {editingId === s.id ? (
                                            <select
                                                value={edited.status}
                                                onChange={(e) => handleChange(e, setEdited, "status")}
                                            >
                                                <option value="true">Активний</option>
                                                <option value="false">Неактивний</option>
                                            </select>
                                        ) : s.status ? "Активний" : "Неактивний"}
                                    </td>
                                    <td className="actions">
                                        {editingId === s.id ? (
                                            <>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleSave(s.id)}
                                                    title="Зберегти"
                                                >
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button
                                                    className="icon-button"
                                                    onClick={handleCancelEdit}
                                                    title="Скасувати"
                                                >
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="icon-button"
                                                onClick={() => handleEdit(s)}
                                                title="Редагувати"
                                            >
                                                <Edit className="icon-btn" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SocketsAdmins;
